<template lang="pug">
  .micropackage(v-if="cards.length > 1" ref="root" data-cnstrc-product-detail data-cnstrc-add-all-to-cart="true")
    h3 {{__('Frequently Bought Together')}}
    .mp-container(:class="cssClass")
      .mp-items
        .mp-item-holder(
          v-for="(card, cardIndex) in cards"
          :key="card.product.productId"
          :data-cnstrc-item-id="card.selected && !card.oos && card.buyRequest.qty > 0 ? (card.buyRequest.metadata?.groupedProductId || card.product.productId) : undefined"
          :data-cnstrc-item-name="card.selected && !card.oos && card.buyRequest.qty > 0 ? card.product.name : undefined"
          :data-cnstrc-item-variation-id="card.selected && !card.oos && card.buyRequest.qty > 0 ? getCardVariationId(card) : undefined"
          :data-cnstrc-item-price="card.selected && !card.oos && card.buyRequest.qty > 0 ? card.price : undefined"
        )
          .mp-item(:class="card.selected && 'selected'")
            .card(@click="goTo(card.target)")
              StaticImage.img(:src="rhpaSelectedImage(card) ? rhpaSelectedImage(card) : (card.product.thumbnail || card.target.thumbnail)" :width="250" :height="250" alt="")
              .name {{card.product.name}}
            configuration.micro-configuration(
              :buyRequest="card.buyRequest"
              :key="`${card.buyRequest.productId}-${card.buyRequest.children?.[0]?.productId || 'root'}`"
              :microConfig="true"
              :class="cssClass"
            )
            Price(:buy-request="card.buyRequest" :show-excl-vat="true")
            Checkbox(
              :label="cardIndex === 0 ? 'This Item' : (card.selected ? 'Selected' : 'Unselected')"
              v-if="!card.oos"
              :id="`bought-together-checkbox-'${card.product.productId}`"
              :disabled="card.oos || card.disabled"
              v-model="card.selected"
              @update:modelValue="validateQtyOnSelect(card)" 
            )
            .oos(v-else) {{__('Out of Stock')}}
      .mp-pricing
        h4(v-if="total === 0") {{__('No items selected')}}
        h4(v-else-if="selected === 1") {{__('Pricing for 1 Item')}}
        h4(v-else-if="selected === 2 && cards.length === 2") {{__('Pricing for Both Items')}}
        h4(v-else-if="selected === cards.length") {{ __('Pricing for All {count} Items', {count: selected}) }}
        h4(v-else) {{ __('Pricing for {count} Items', {count: selected}) }}
        .price-box
          .price-line
            .total {{__('Total Price')}}
            .price {{$rogue.currency(total, { euTaxClass: true })}}
          RogueButton(
            color="red"
            width="full"
            @click="addToList('cart')"
            :disabled="total === 0"
            data-cnstrc-btn="add_to_cart"
          ) {{__('Add to Cart')}}
          add-to-wishlist(:buyRequest="buyRequests")
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { computePriceSync } from '@/js/helpers/price-utils'
import { createBuyRequest, createBuyRequestAsync, getBuyRequestMeta, getSimpleForBuyRequest } from '@/js/helpers/buy-request'
import AddToWishlist from '@/js/components/add-to-wishlist.vue'
import stockStatus from '@/js/helpers/stockStatus'
import { useRogue } from '@/js/services/Rogue'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'

type Card = {
  buyRequest: BuyRequest,
  product: Product,
  target: Product,
  selected: boolean,
  oos: boolean,
  disabled: boolean,
  price?: number,
  options?: any[],
  title?: string
}

const props = defineProps < {
  buyRequest: BuyRequest
}>()

const cards = ref<Card[]>([])

const root = ref<HTMLDivElement | null>(null)

const router = useRouter()
const rogue = useRogue()
const listStore = useListStore()
const productStore = useProductStore()

const products = computed(() => productStore.products)

const errors = computed(() => productStore.errors)

const cssClass = computed<string>(() => (cards.value.length === 1 && 'one-item')
    || (cards.value.length === 2 && 'two-items')
    || (cards.value.length === 3 && 'three-items')
    || 'four-or-more')

const selected = computed<number>(() => cards.value.filter(c => c.selected && !c.oos).length)

const buyRequests = computed<BuyRequest[]>(() => cards.value.filter(c => c.selected && !c.oos).map(c => c.buyRequest))

const total = computed<number>(() => {
  if (props.buyRequest.loading) {
    return 0
  }

  let val = 0
  cards.value
    .filter(c => c.selected && !c.oos)
    .forEach(card => val += (card.price || 0))

  return val
})

const product = computed<Product>(() => products.value[props.buyRequest.productId])

const mainBuyRequest = computed<BuyRequest>(() => {
  // We dont want the first card to show the price for the entire group's buy request, just the main product
  if (product.value.type === 'grouped' && props.buyRequest.children) {
    return props.buyRequest.children[0]
  }

  return props.buyRequest
})

function getCardVariationId(card: Card): string | number | undefined {
  if (card.product.type === 'bundle') {
    const bundleOptions = (card.product as any).bundleOptions?.map((opt: any) =>
      card.buyRequest.children?.find(child => child.metadata?.bundleOptionId === opt.id)
    )
    if (!bundleOptions?.length) return undefined

    return bundleOptions.map((child: any) => (!child ? 0 : getSimpleForBuyRequest(child, rogue)?.productId)).join('-')
  }

  const childId = card.buyRequest.children?.[0]?.productId

  return childId !== undefined && childId !== card.product.productId ? childId : undefined
}

async function goTo(goToProduct: Product) {
  if (goToProduct.visibility === 4 && goToProduct.url) {
    router.push(goToProduct.url)
  } else {
    const realBuyRequest = await createBuyRequestAsync(goToProduct.productId, rogue)

    const meta = getBuyRequestMeta(realBuyRequest, rogue)

    if (meta.linkPath) {
      router.push(meta.linkPath)
    }
  }
}

const rhpaSelectedImage = (card: Card) => {
  const selectedProdId = card.buyRequest?.children?.[0].productId 

  if (selectedProdId && products.value[selectedProdId]) {

    return products.value[selectedProdId].thumbnail
  }

  return card.product.thumbnail
}

async function load() {
  const ids = [mainBuyRequest.value.productId].concat(product.value.micropackageIds || [])
  
  await productStore.loadAll(ids, rogue)

  const childrenToLoad: number[] = []
  ids.forEach(id => {
    const p = products.value[id]
    if (p?.type === 'configurable' && p.childrenIds) {
      const missing = p.childrenIds.filter(childId => !products.value[childId])
      childrenToLoad.push(...missing)
    }
  })

  if (childrenToLoad.length > 0) {
    await productStore.loadAll(childrenToLoad, rogue)
  }

  cards.value = await Promise.all(ids.map(id => products.value[id])
    .filter(p => !!p)
    .map(async (p: Product, i: number) => {
      /**
       * Some of these micropackage products are simples within a group - meaning they dont have their own URL.
       * We need to use the async mode of createBuyRequest, which will check whether the product passed in is
       * actually a valid root product for a buy request, and if not will add the relevant parent data.
       */
      let buyRequest = (i === 0 ? mainBuyRequest.value : await createBuyRequestAsync(p.productId, rogue))
      if (i === 0 && buyRequest.qty !== 1) {
        buyRequest = JSON.parse(JSON.stringify(buyRequest))
        buyRequest.qty = 1
      }
      
      const oos = stockStatus.forBuyRequest(buyRequest, products.value) !== ''
      let target = p

      if (target.visibility === 1) {
        // If there is a grouped product on the buy request, use that as the landing page
        if (buyRequest.metadata?.groupedProductId) {
          target = products.value[buyRequest.metadata.groupedProductId] || target
        }

        // If this is a simple of a configurable, use the configurable product as the target
        if (target.visibility === 1 && buyRequest.productId !== target.productId) {
          target = products.value[buyRequest.productId] || target
        }
      }

      return {
        buyRequest,
        product  : p,
        target,
        selected : !oos,
        oos,
        disabled : false,
        price    : computePriceSync(buyRequest, rogue.pinia!),
      }
    }))
}

async function addToList(list: string) {
  await listStore.addListItems(rogue, list, buyRequests.value)
  rogue.track.track({ type: 'custom', event: 'Add to Cart from Micropackage', data: { items: buyRequests.value } })
}

function syncCousinConfiguration(targetChildId: number | undefined) {
  if (typeof targetChildId !== 'number' || !products.value[targetChildId]) {
    return
  }

  const targetProduct = products.value[targetChildId]
  const targetSku = targetProduct.sku

  cards.value.forEach((card, i) => {
    card.price = computePriceSync(card.buyRequest, rogue.pinia!)
    const isOutOfStock = stockStatus.forBuyRequest(card.buyRequest, products.value) !== ''
    card.oos = isOutOfStock
    if((i === 0 && card.buyRequest.qty === 0)) card.selected = false
    if(card.product.childrenIds?.includes(targetChildId)){
      const baseUrl = card.target.url?.split('?')[0]
      card.target.url = `${baseUrl}?sku=${targetSku}`
    }
  })
}

function validateQtyOnSelect (card: Card) {
  if (card.selected && card.buyRequest.qty === 0) {
    card.buyRequest.qty = 1
  }
}

watch(() => mainBuyRequest.value, (newVal, oldVal) => {
  if (!rogue.ssr && newVal?.productId !== oldVal?.productId) {
    load()
  }

  syncCousinConfiguration(mainBuyRequest.value.children?.[0]?.productId)
 
}, { deep: true })

watch(
  () => cards.value.map(card => card.buyRequest.children?.map(c => c.productId)),
  (newValue, oldValue) => {
    const flatNew = newValue.flat()
    const flatOld = oldValue ? oldValue.flat() : []
    const changedId = flatNew.find((id, index) => id !== flatOld[index])

    if (changedId) {
      syncCousinConfiguration(changedId)
    }
  }, 
  { deep: true }
)

function createPlaceholders() {
  // We set up the cards during SSR and pre-mounted as simpler placeholders, since creating the real buy requests
  // may need to load async products to find parents of simple products, which is not worth doing for hydration

  if (product.value?.micropackageIds?.length) {
    cards.value = [props.buyRequest.productId]
      .concat(product.value.micropackageIds)
      .map(productId => products.value[productId] || { productId, name: '' })
      .map((p, i) => ({
        product    : p,
        buyRequest : i === 0 ? mainBuyRequest.value : createBuyRequest(p, rogue),
        target     : p,
        selected   : false,
        oos        : false,
        disabled   : true,
      }))
  } else {
    cards.value = []
  }
}

if (!rogue.ssr) {
  createPlaceholders()
}

onServerPrefetch(async () => {
  await productStore.loadAll(product.value.micropackageIds || [], rogue)
  createPlaceholders()
})

onMounted(async () => {
  await nextTick()
  load()
})

defineExpose({
  $el: root,
})
</script>
<style lang="scss" scoped>

.micropackage{
  @include mobile(){
    max-width: 100%;
  }
}

.mp-items {
  display: grid;
  grid-template-columns: repeat(3, 1fr); 
  grid-template-rows: auto auto auto auto auto; 
  align-items: start;
  margin-bottom: $space-xs;
  padding-bottom: $space-xs;
  overflow-y: hidden;
  overflow-x: auto;
  gap: $space-s;
}

.two-items {
  .mp-items {
    grid-template-columns: repeat(2, 1fr);
  }
}

.four-or-more{
  .mp-items {
    grid-template-columns: repeat(3, 1fr);
  }
}

.mp-item{
  height: 100%;
  display: flex;
  flex-direction: column;
}

.product-price{
  grid-row: 4;
  height: auto;
  padding: 8px 0;
  min-height: 45px;
  @include mobile(){
    min-height: 30px;
  }
}

.four-or-more {
  @include mobile(){
    .mp-item{
      min-width: 85px;
    }
    .mp-items{
      width: 90%;
      max-width: 100vw;
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

.mp-item-holder {
  @include mobile(){
    min-width: 100px;
  }
  display: grid;
  grid-row: span 5;
  grid-template-rows: subgrid;
}

.oos {
  margin-top: $space-s;
  font-size: $size-xs;
  color: $color-gray-dark;
}

.mp-item {
  .img {
    opacity: 0.4;
  }

  &.selected .img {
    opacity: 1;
  }

  display: grid;
  grid-row: span 5;
  grid-template-rows: subgrid;
}

.card {
  color: $color-black;
  text-decoration: none;
  cursor: pointer;
}

.name {
  margin-top: $space-xxs;
  font-size: $size-xs;
  line-height: $size-s;
  min-height: 35px;
}

.price {
  font-weight: $weight-bold;
  font-size: $size-xs;
}

.mp-pricing {
  margin-left: -$space-s;
  margin-right: -$space-s;
  background-color: $color-gray-x-light;
  padding: $space-s;
  width: 100%;
  margin: auto;

  .price {
    font-size: $size-s;
    font-family: $font-condensed;
  }
}

.price-box {
  background: $color-white;
  padding: $space-s;
}

.price-line {
  display: flex;
  justify-content: space-between;
  margin-bottom: $space-xs;
}

.wishlist {
  margin-top: $space-m;
  display: flex;
  font-size: $size-xs;
  color: $color-black;
  align-items: center;
  justify-content: center;

  .icon {
    width: 16px;
    margin-right: $space-xxs;
  }
}

.mp-item .checkbox, .oos{
  grid-row: 5;
  height: auto;
  align-self: end;
}

@include not-mobile() {
  .mp-container {
    display: flex;
    flex-wrap: wrap;
  }

  .mp-items {
    gap: $space-s;
    row-gap: $space-xxs;
    padding-right: $space-s;
  }

  .mp-item {
    margin-bottom: $space-l;
  }

  .mp-items {
    flex-grow: 1;
    flex-wrap: wrap;
    overflow: hidden;
  }

  .one-item {
    .mp-items {
      max-width: 163px + $space-s;
    }
  }

  .two-items {
    .mp-items {
      max-width: (163px + $space-s) * 2;
    }
  }

  .three-items {
    .mp-items {
      max-width: (163px + $space-s) * 3;
    }
  }

  .mp-pricing {
    flex: 0 0 200px;
    margin-top: 0;
    margin-left: 0;
    margin-right: 0;
    padding: 0;
    background-color: $color-white;

    h4 {
      padding-bottom: $space-s;
      border-bottom: $border-width-thin solid $color-gray;
    }
  }

  .wishlist {
    justify-content: flex-start;
  }

  .price-box {
    padding: 0;
  }
}

@include all-desktop() {
  .four-or-more {
    .mp-item-holder {
      flex: 0 0 50%;
    }

    .mp-items {
      max-width: (163px + $space-s) * 2;
    }
  }
}

@media (min-width: 1280px) and (max-width: $largeBp) {
  .four-or-more {
    .mp-item-holder {
      flex: 0 0 25%;
    }

    .mp-items {
      grid-template-columns: repeat(3, 1fr);
      max-width: (142px + $space-s) * 4;
    }
  }
}

@include xl() {
  .mp-items {
    gap: $space-l;
    row-gap: $space-xs;
    padding-right: $space-l;
  }

  .one-item .mp-items {
    max-width: 241px + $space-l;
  }

  .two-items .mp-items {
    max-width: (241px + $space-l) * 2;
  }

  .three-items .mp-items {
    max-width: (241px + $space-l) * 3;
  }

  .four-or-more .mp-items {
    max-width: (241px + $space-l) * 4;
    grid-template-columns: repeat(4, 1fr);
  }

  .four-or-more {
    .mp-item-holder {
      flex: 0 0 20%;
    }
  }
}

</style>
